import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useAdminLanguage } from '../AdminLanguageContext';

const STATUS_BADGE = {
  pending: 'badge-blue',
  reviewing: 'badge-gray',
  resolved: 'badge-green',
  rejected: 'badge-red',
};

export default function Reports() {
  const { copy } = useAdminLanguage();
  const reportsCopy = copy.reports;
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('all');
  const [targetType, setTargetType] = useState('all');
  const [keyword, setKeyword] = useState('');
  const [notes, setNotes] = useState({});
  const [updatingId, setUpdatingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    api.get('/admin/reports', {
      params: {
        status,
        targetType,
        keyword,
      },
    })
      .then(res => setReports(res.data.reports || []))
      .catch(err => setError(err.response?.data?.error || reportsCopy.errors.loadFailed))
      .finally(() => setLoading(false));
  }, [status, targetType, keyword, reportsCopy.errors.loadFailed]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => ({
    total: reports.length,
    pending: reports.filter(item => item.status === 'pending').length,
    reviewing: reports.filter(item => item.status === 'reviewing').length,
    resolved: reports.filter(item => item.status === 'resolved').length,
  }), [reports]);

  const updateStatus = async (id, nextStatus) => {
    setUpdatingId(id);
    setError('');
    try {
      await api.post(`/admin/reports/${id}/status`, {
        status: nextStatus,
        adminNote: notes[id] || '',
      });
      setNotes(prev => ({ ...prev, [id]: '' }));
      load();
    } catch (err) {
      setError(err.response?.data?.error || reportsCopy.errors.updateFailed);
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div>
      <div className="page-title">{reportsCopy.title}</div>
      {error && <div className="card" style={{ color: '#b91c1c', marginBottom: 16 }}>{error}</div>}

      <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
        <div className="card stat-card"><div className="stat-label">{reportsCopy.stats.total}</div><div className="stat-value">{stats.total}</div></div>
        <div className="card stat-card"><div className="stat-label">{reportsCopy.stats.pending}</div><div className="stat-value" style={{ color: '#3b82f6' }}>{stats.pending}</div></div>
        <div className="card stat-card"><div className="stat-label">{reportsCopy.stats.reviewing}</div><div className="stat-value" style={{ color: '#6b7280' }}>{stats.reviewing}</div></div>
        <div className="card stat-card"><div className="stat-label">{reportsCopy.stats.resolved}</div><div className="stat-value" style={{ color: '#1dbf73' }}>{stats.resolved}</div></div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div className="search-input">
            <input placeholder={reportsCopy.searchPlaceholder} value={keyword} onChange={e => setKeyword(e.target.value)} />
          </div>
          <select value={status} onChange={e => setStatus(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6 }}>
            <option value="all">{reportsCopy.allStatus}</option>
            <option value="pending">{reportsCopy.status.pending}</option>
            <option value="reviewing">{reportsCopy.status.reviewing}</option>
            <option value="resolved">{reportsCopy.status.resolved}</option>
            <option value="rejected">{reportsCopy.status.rejected}</option>
          </select>
          <select value={targetType} onChange={e => setTargetType(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6 }}>
            <option value="all">{reportsCopy.allTypes}</option>
            <option value="service">{reportsCopy.type.service}</option>
            <option value="product">{reportsCopy.type.product}</option>
          </select>
          <span style={{ fontSize: 13, color: '#6b7280', alignSelf: 'center', marginLeft: 'auto' }}>{reportsCopy.showing(reports.length)}</span>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{reportsCopy.columns.id}</th><th>{reportsCopy.columns.type}</th><th>{reportsCopy.columns.target}</th><th>{reportsCopy.columns.reporter}</th><th>{reportsCopy.columns.reason}</th><th>{reportsCopy.columns.detail}</th><th>{reportsCopy.columns.status}</th><th>{reportsCopy.columns.adminNote}</th><th>{reportsCopy.columns.createdAt}</th><th>{reportsCopy.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>{reportsCopy.loading}</td></tr>
              ) : reports.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>{reportsCopy.empty}</td></tr>
              ) : reports.map(item => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{reportsCopy.type[item.target_type] || item.target_type}</td>
                  <td style={{ fontWeight: 600 }}>{item.target_title || `#${item.target_id}`}</td>
                  <td>{item.reporter_name || '-'}</td>
                  <td style={{ maxWidth: 180 }}>{item.reason}</td>
                  <td style={{ maxWidth: 220, color: '#555' }}>{item.detail || '-'}</td>
                  <td><span className={`badge ${STATUS_BADGE[item.status] || 'badge-gray'}`}>{reportsCopy.status[item.status] || item.status}</span></td>
                  <td>
                    <input
                      aria-label={reportsCopy.adminNoteAria(item.id)}
                      value={notes[item.id] ?? item.admin_note ?? ''}
                      onChange={e => setNotes(prev => ({ ...prev, [item.id]: e.target.value }))}
                      placeholder={reportsCopy.adminNote}
                      style={{ width: 160, padding: '7px 9px', border: '1px solid #e5e7eb', borderRadius: 6 }}
                    />
                  </td>
                  <td style={{ color: '#6b7280' }}>{new Date(item.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {item.status !== 'reviewing' && <button className="btn btn-sm btn-outline" disabled={updatingId === item.id} onClick={() => updateStatus(item.id, 'reviewing')}>{reportsCopy.actions.reviewing}</button>}
                      {item.status !== 'resolved' && <button className="btn btn-sm btn-primary" disabled={updatingId === item.id} onClick={() => updateStatus(item.id, 'resolved')}>{reportsCopy.actions.resolved}</button>}
                      {item.status !== 'rejected' && <button className="btn btn-sm btn-danger" disabled={updatingId === item.id} onClick={() => updateStatus(item.id, 'rejected')}>{reportsCopy.actions.rejected}</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
